import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listarPartesTrabajo } from "../utils/parteTrabajoApi";
import { getStoredRol, normalizeRol } from "../utils/authSession";
import EstadoCochesPage from "./EstadoCochesPage";
import { Alert } from "../components/ui";

const ICONS = {
  back: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  list: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  ),
  chevron: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  clock: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  user: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  alert: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  check: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  play: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="5 3 19 12 5 21 5 3" />
    </svg>
  ),
};

const ACCENT = "#6366f1";

const normalizeParteEstado = (estado = "") => {
  const value = String(estado || "").trim().toLowerCase();
  if (value === "nuevo") return "pendiente";
  if (value === "pausado") return "en_pausa";
  if (value === "completado") return "finalizado";
  if (value === "entreg") return "entregado";
  return value;
};

const matchesParteEstado = (parte, ...expected) => expected.includes(normalizeParteEstado(parte?.estado));

const getParteReferenceDate = (parte) => (
  parte?.fecha_inicio || parte?.fecha_fin || parte?.created_at || parte?.fecha_creacion || null
);

const ESTADO_CARD_META = {
  pendiente:   { color: "#818cf8", border: "rgba(129,140,248,0.35)", bg: "rgba(129,140,248,0.07)", label: "Pendiente" },
  en_proceso:  { color: "#f97316", border: "rgba(249,115,22,0.35)",  bg: "rgba(249,115,22,0.07)",  label: "En proceso" },
  en_pausa:    { color: "#f59e0b", border: "rgba(245,158,11,0.35)",  bg: "rgba(245,158,11,0.07)",  label: "En pausa"   },
  en_calidad:  { color: "#38bdf8", border: "rgba(56,189,248,0.35)",  bg: "rgba(56,189,248,0.07)",  label: "En calidad" },
  finalizado:  { color: "#22c55e", border: "rgba(34,197,94,0.35)",   bg: "rgba(34,197,94,0.07)",   label: "Completado" },
};

function VehicleTimeline({ parte }) {
  const estadoNorm = normalizeParteEstado(parte?.estado);
  const meta = ESTADO_CARD_META[estadoNorm] || ESTADO_CARD_META.pendiente;
  const matricula = parte.matricula || parte.coche_matricula || "---";
  const vehiculo = [parte.marca || parte.coche_marca || "", parte.modelo || parte.coche_modelo || ""].join(" ").trim() || "Vehículo";
  const cliente = parte.cliente_nombre || parte.coche_cliente_nombre || "Sin cliente";
  const durMin = parte.duracion_minutos ?? parte.tiempo_estimado_minutos ?? null;

  return (
    <div style={{
      background: "var(--sw-surface)",
      border: `1px solid ${meta.border}`,
      borderLeft: `3px solid ${meta.color}`,
      borderRadius: "12px",
      padding: "0.85rem 1rem",
      display: "flex",
      flexDirection: "column",
      gap: "0.4rem",
    }}>
      {/* Cabecera: matrícula + estado */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
        <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "0.95rem", color: "var(--sw-accent)", letterSpacing: "0.08em", background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: "5px", padding: "0.05rem 0.5rem" }}>
          {matricula}
        </span>
        <span style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`, borderRadius: "999px", padding: "0.15rem 0.65rem", fontSize: "0.7rem", fontWeight: 700, whiteSpace: "nowrap" }}>
          {meta.label}
        </span>
      </div>

      {/* Vehículo */}
      <div style={{ fontSize: "0.84rem", fontWeight: 600, color: "var(--sw-text)" }}>{vehiculo}</div>

      {/* Cliente */}
      <div style={{ fontSize: "0.77rem", color: "var(--sw-muted)" }}>👤 {cliente}</div>

      {/* Empleado */}
      {parte.empleado_nombre && (
        <div style={{ fontSize: "0.77rem", color: "var(--sw-text)" }}>🔧 {parte.empleado_nombre}</div>
      )}

      {/* Tipo tarea + fase */}
      <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap", marginTop: "0.1rem" }}>
        {parte.tipo_tarea && (
          <span style={{ background: "rgba(212,175,55,0.1)", color: "var(--sw-accent)", border: "1px solid rgba(212,175,55,0.2)", borderRadius: "4px", padding: "0.06rem 0.5rem", fontSize: "0.7rem", fontWeight: 700 }}>
            {parte.tipo_tarea}
          </span>
        )}
        {parte.fase && (
          <span style={{ background: "rgba(255,255,255,0.05)", color: "var(--sw-muted)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", padding: "0.06rem 0.5rem", fontSize: "0.7rem" }}>
            {parte.fase}
          </span>
        )}
        {durMin !== null && (
          <span style={{ background: "rgba(255,255,255,0.04)", color: "var(--sw-muted)", borderRadius: "4px", padding: "0.06rem 0.5rem", fontSize: "0.7rem" }}>
            ⏱ {durMin < 60 ? `${durMin} min` : `${Math.floor(durMin / 60)}h ${durMin % 60 > 0 ? `${durMin % 60}m` : ""}`}
          </span>
        )}
      </div>

      {/* Observaciones */}
      {parte.observaciones && (
        <div style={{ fontSize: "0.77rem", color: "var(--sw-muted)", fontStyle: "italic", marginTop: "0.1rem" }}>
          {parte.observaciones}
        </div>
      )}

    </div>
  );
}

function StatsCard({ label, value, color = ACCENT, icon = null }) {
  return (
    <div style={{
      background: "var(--sw-surface)",
      border: "1px solid rgba(255,255,255,0.07)",
      borderRadius: "10px",
      padding: "1rem",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    }}>
      {icon && (
        <div style={{ width: "38px", height: "38px", background: `${color}18`, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color, flexShrink: 0 }}>
          {icon}
        </div>
      )}
      <div>
        <div style={{ fontSize: "0.72rem", color: "var(--sw-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <div style={{ fontSize: "1.5rem", fontWeight: 700, color }}>{value}</div>
      </div>
    </div>
  );
}

function CalidadPartesAcompanamientoView() {
  const [partes, setPartes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    enProgreso: 0,
    enPausa: 0,
    pendiente: 0,
    completado: 0,
    enCalidad: 0,
  });

  const cargarPartes = async () => {
    try {
      setCargando(true);
      const data = await listarPartesTrabajo();
      const lista = Array.isArray(data) ? data : [];

      const hoy = new Date().toISOString().split("T")[0];
      const partesVisibles = lista.filter((parte) => {
        if (matchesParteEstado(parte, "pendiente", "en_proceso", "en_pausa", "en_calidad")) {
          return true;
        }
        const refDate = getParteReferenceDate(parte);
        if (!refDate) return false;
        return String(refDate).split("T")[0] === hoy;
      });

      setPartes(partesVisibles);

      // Calcular stats
      const statsObj = {
        total: partesVisibles.length,
        enProgreso: partesVisibles.filter((p) => matchesParteEstado(p, "en_proceso")).length,
        enPausa: partesVisibles.filter((p) => matchesParteEstado(p, "en_pausa")).length,
        pendiente: partesVisibles.filter((p) => matchesParteEstado(p, "pendiente")).length,
        completado: partesVisibles.filter((p) => matchesParteEstado(p, "finalizado")).length,
        enCalidad: partesVisibles.filter((p) => matchesParteEstado(p, "en_calidad")).length,
      };
      setStats(statsObj);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error("Error cargando partes:", err);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargarPartes();
    const intervalo = setInterval(cargarPartes, 10000); // Actualizar cada 10s
    return () => clearInterval(intervalo);
  }, []);

  const partesOrdenados = {
    enProgreso: partes.filter((p) => matchesParteEstado(p, "en_proceso")),
    enPausa: partes.filter((p) => matchesParteEstado(p, "en_pausa")),
    enCalidad: partes.filter((p) => matchesParteEstado(p, "en_calidad")),
    completado: partes.filter((p) => matchesParteEstado(p, "finalizado")),
    pendiente: partes.filter((p) => matchesParteEstado(p, "pendiente")),
  };

  return (
    <div className="sw-veh-wrapper">
      <div className="sw-veh-hero">
        <div className="container sw-veh-hero-inner">
          <Link to="/" className="sw-veh-back" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", color: ACCENT }}>
            {ICONS.back}
            <span>Inicio</span>
          </Link>
          <div className="sw-veh-hero-body">
            <span
              className="sw-veh-hero-icon"
              style={{
                background: `${ACCENT}15`,
                borderColor: `${ACCENT}30`,
                color: ACCENT,
              }}
            >
              {ICONS.list}
            </span>
            <div>
              <h1 className="sw-veh-hero-title">Acompañamiento de trabajos</h1>
              <p className="sw-veh-hero-sub">Supervisión en tiempo real del flujo de coches</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container sw-veh-content">
        {/* STATS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "12px",
            marginBottom: "32px",
          }}
        >
          <StatsCard label="Trabajos hoy" value={stats.total} icon={ICONS.clock} />
          <StatsCard
            label="En progreso"
            value={stats.enProgreso}
            color="#ef4444"
            icon={ICONS.play}
          />
          <StatsCard
            label="En pausa"
            value={stats.enPausa}
            color="#f59e0b"
            icon={ICONS.alert}
          />
          <StatsCard
            label="Completados"
            value={stats.completado}
            color="#10b981"
            icon={ICONS.check}
          />
        </div>

        {error && (
          <Alert variant="danger">
            {ICONS.alert}
            {error}
          </Alert>
        )}

        {cargando && !partes.length && (
          <div
            style={{
              background: "white",
              padding: "40px",
              borderRadius: "10px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            Cargando trabajos...
          </div>
        )}

        {/* EN PROGRESO */}
        {partesOrdenados.enProgreso.length > 0 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <h2 style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--sw-text)", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <span style={{ width: 8, height: 8, background: "#f97316", borderRadius: "50%", animation: "pulse 2s infinite" }} />
              En proceso ({partesOrdenados.enProgreso.length})
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "0.75rem" }}>
              {partesOrdenados.enProgreso.map((parte) => <VehicleTimeline key={parte.id} parte={parte} />)}
            </div>
          </div>
        )}

        {/* EN PAUSA */}
        {partesOrdenados.enPausa.length > 0 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <h2 style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--sw-text)", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <span style={{ width: 8, height: 8, background: "#f59e0b", borderRadius: "50%" }} />
              En pausa ({partesOrdenados.enPausa.length})
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "0.75rem" }}>
              {partesOrdenados.enPausa.map((parte) => <VehicleTimeline key={parte.id} parte={parte} />)}
            </div>
          </div>
        )}

        {/* EN CALIDAD */}
        {partesOrdenados.enCalidad.length > 0 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <h2 style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--sw-text)", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <span style={{ width: 8, height: 8, background: "#38bdf8", borderRadius: "50%" }} />
              En calidad ({partesOrdenados.enCalidad.length})
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "0.75rem" }}>
              {partesOrdenados.enCalidad.map((parte) => <VehicleTimeline key={parte.id} parte={parte} />)}
            </div>
          </div>
        )}

        {/* PENDIENTES */}
        {partesOrdenados.pendiente.length > 0 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <h2 style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--sw-text)", marginBottom: "0.75rem", display: "flex", alignItems: "center", gap: "0.5rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              <span style={{ width: 8, height: 8, background: "#818cf8", borderRadius: "50%" }} />
              Pendientes ({partesOrdenados.pendiente.length})
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "0.75rem" }}>
              {partesOrdenados.pendiente.map((parte) => <VehicleTimeline key={parte.id} parte={parte} />)}
            </div>
          </div>
        )}

        {/* COMPLETADOS */}
        {partesOrdenados.completado.length > 0 && (
          <div style={{ marginBottom: "1.75rem" }}>
            <h2 style={{ fontSize: "0.85rem", fontWeight: 700, color: "var(--sw-muted)", marginBottom: "0.75rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Completados hoy ({partesOrdenados.completado.length})
            </h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: "0.75rem" }}>
              {partesOrdenados.completado.map((parte) => <VehicleTimeline key={parte.id} parte={parte} />)}
            </div>
          </div>
        )}

        {!cargando && partes.length === 0 && (
          <div style={{ background: "rgba(99,102,241,0.05)", border: "2px dashed rgba(99,102,241,0.2)", padding: "2.5rem", borderRadius: "12px", textAlign: "center", color: "var(--sw-muted)" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }}>📋</div>
            <p style={{ fontSize: "1rem", fontWeight: 600, color: "var(--sw-text)", margin: 0 }}>Sin trabajos hoy</p>
            <p style={{ fontSize: "0.85rem", marginTop: "0.4rem", margin: "0.4rem 0 0" }}>Los trabajos aparecerán aquí cuando se creen nuevos partes.</p>
          </div>
        )}
      </div>

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}

export default function AdminPartesTrabajoAcompanamiento() {
  const rol = normalizeRol(getStoredRol());
  if (["administrador", "calidad", "detailing"].includes(rol)) {
    return <EstadoCochesPage />;
  }
  return <CalidadPartesAcompanamientoView />;
}
